// Personregister: rosteren som viser HVEM er bundet til HVILKEN ORBAT-node,
// og om en overordnet har VERIFISERT tilhørigheten.
//
// ── HVORFOR DENNE FILA FINNES ────────────────────────────────────────────────
// Hele poenget med grenen den kom fra var: «kan jeg koble og verifisere
// brukere til deler av vår ORBAT?» HTTP-flaten (`/admin/brukere`,
// `/admin/medlemskap/*` i services/hub/src/server.ts) og myndighetsregelen
// (`kanVerifisere` i @skade/rbac — overordnet i DIREKTE LINJE, ikke en generisk
// admin-rolle) var ferdige og testet — men INGEN operatør kunne nå dem. En
// verifiseringsflyt ingen kan trykke på, er ikke en verifiseringsflyt.
//
// ── TO TING DENNE FLATA IKKE FÅR LYVE OM (jf. arkiv.ts) ─────────────────────
//  1. EN FEILET HENTING ER IKKE ET TOMT REGISTER. Et 401/nettverksbrudd skal
//     IKKE se ut som «ingen personer er registrert».
//  2. ET AVSLAG SKAL VISE SERVERENS EGEN GRUNN. `kanVerifisere` er en
//     kommandolinje-sjekk (ikke en rolle-sjekk), og avslagstekstene
//     (`VERIFISERING_AVSLAG_TEKST`) er allerede skrevet for et menneske å lese
//     — panelet skal ALDRI erstatte dem med en generisk «avvist».
//
// Docked panel, hooks-injisert nettverk (testbart uten fetch — se
// personregister.dom.test.ts), ren DOM. Samme mønster som ui/admin.ts og
// ui/arkiv.ts.
import { PersonBindingSchema, personEtikett, type PersonBinding } from '@skade/contracts';

/**
 * DoS-safe cap: hubben har i teorien opp mot `MAX_HENDELSER` (200 000)
 * medlemskapshendelser å redusere (medlemskap-lager.ts) — å rendre en
 * ubegrenset DOM-liste er en åpen invitasjon til å fryse fanen. Rammen dekker
 * et helt distrikt med god margin; blir den for smal en dag, økes den her.
 */
export const MAKS_RADER = 500;

export interface BrukereSvar {
  bindinger: PersonBinding[];
  /** Satt når svaret ikke hadde formen vi forventet — vises, ikke svelges. */
  formfeil?: string;
}

/**
 * Les `GET /admin/brukere`-svaret.
 *
 * ⚠ VALIDERER MOT SAMME SCHEMA SOM SERVEREN SKRIVER (`PersonBindingSchema`) —
 * ikke en hånd-rullet typevakt som kan gå ut av sync med kontrakten. Poster
 * som ikke validerer forkastes ENKELTVIS og telles i `formfeil`, i stedet for
 * å kaste bort resten av lista.
 */
export function lesBrukereSvar(u: unknown): BrukereSvar {
  const o = (u ?? {}) as Record<string, unknown>;
  const raa = o['bindinger'];
  if (!Array.isArray(raa)) {
    return {
      bindinger: [],
      formfeil: 'Svaret fra hubben hadde ikke et «bindinger»-felt — dette er IKKE et tomt register.',
    };
  }
  const bindinger: PersonBinding[] = [];
  let forkastet = 0;
  for (const x of raa) {
    const p = PersonBindingSchema.safeParse(x);
    if (p.success) bindinger.push(p.data); else forkastet += 1;
  }
  return {
    bindinger,
    ...(forkastet > 0 ? { formfeil: `${forkastet} post(er) i svaret var ugyldige og er utelatt.` } : {}),
  };
}

/**
 * Ikke-verifiserte personer FØRST — det er de som krever en handling fra en
 * overordnet, og en kommandant skal ikke måtte scrolle forbi et ferdig
 * verifisert kompani for å finne den ene nye rekrutten. Likt sortert etter
 * etikett innen hver gruppe, for en stabil og lesbar rekkefølge.
 */
export function sorterBindinger(bindinger: readonly PersonBinding[]): PersonBinding[] {
  return [...bindinger].sort((a, b) => {
    if (a.verifisert !== b.verifisert) return a.verifisert ? 1 : -1;
    return personEtikett(a).localeCompare(personEtikett(b), 'nb-NO');
  });
}

export interface PersonregisterHooks {
  /** Autentisert GET. Injisert slik at panelet er testbart uten nettverk. */
  hent: (sti: string) => Promise<unknown>;
  /** Autentisert POST med JSON-kropp. */
  send: (sti: string, kropp: unknown) => Promise<unknown>;
}

export class PersonregisterPanel {
  readonly el: HTMLElement;
  private open = false;
  private kvitteringEl!: HTMLElement;
  private listeEl!: HTMLElement;
  private siste: PersonBinding[] = [];
  /** Matrix-id-er med en verifiser/tilbakekall-forespørsel i flukt — sperrer
   * dobbelttrykk på DEN raden uten å låse hele panelet. */
  private ventende = new Set<string>();
  /** Siste avslag/feil PER PERSON — overlever re-tegning til neste forsøk
   * eller neste `last()`, slik at operatøren fortsatt ser hvorfor det feilet
   * etter at knappen har blitt slått av «Sender …» igjen. */
  private feilPerPerson = new Map<string, string>();

  constructor(private hooks: PersonregisterHooks) {
    this.el = this.build();
  }

  toggle() {
    this.open = !this.open;
    this.el.classList.toggle('open', this.open);
    if (this.open) void this.last();
  }

  private build(): HTMLElement {
    const p = div('report-composer personregister-panel');
    const head = div('report-head');
    head.append(span('report-title', 'Personregister'));
    const x = document.createElement('button');
    x.className = 'report-x'; x.textContent = '×'; x.onclick = () => this.toggle();
    head.append(x);
    p.append(head);
    p.append(span('checklist-note',
      'Hvem er bundet til hvilken ORBAT-node — og hvem har en overordnet i direkte linje bekreftet.'));

    const oppdater = document.createElement('button');
    oppdater.type = 'button'; oppdater.className = 'targetlist-btn'; oppdater.textContent = 'Oppdater listen';
    oppdater.onclick = () => void this.last();
    p.append(oppdater);

    this.kvitteringEl = div('checklist-note');
    p.append(this.kvitteringEl);

    this.listeEl = div('targetlist-list');
    p.append(this.listeEl);
    return p;
  }

  /** Hent lista på nytt. Feil VISES; de svelges aldri til «0 personer». */
  async last(): Promise<void> {
    this.kvitteringEl.textContent = 'Henter …';
    try {
      const svar = lesBrukereSvar(await this.hooks.hent('/admin/brukere'));
      this.siste = svar.bindinger;
      this.tegnKvittering(svar);
      this.tegnListe();
    } catch (err) {
      // ⚠ EN FEILET HENTING ER IKKE ET TOMT REGISTER — se filkommentaren.
      this.siste = [];
      this.kvitteringEl.replaceChildren();
      this.kvitteringEl.append(
        feilSpan(`⚠ Kunne ikke lese personregisteret: ${feilTekst(err)}`),
        span('checklist-note', 'Dette er en FEIL, ikke et tomt register.'),
      );
      this.listeEl.replaceChildren();
    }
  }

  private tegnKvittering(svar: BrukereSvar) {
    this.kvitteringEl.replaceChildren();
    const antall = svar.bindinger.length;
    const uverifisert = svar.bindinger.filter((b) => !b.verifisert).length;
    this.kvitteringEl.append(span('checklist-note',
      antall
        ? `${antall} registrert · ${uverifisert} venter på verifisering`
        : 'Ingen personer registrert i ORBAT ennå.'));
    if (svar.formfeil) this.kvitteringEl.append(varselSpan(`⚠ ${svar.formfeil}`));
  }

  private tegnListe() {
    this.listeEl.replaceChildren();
    if (!this.siste.length) return;
    const sortert = sorterBindinger(this.siste);
    const vist = sortert.slice(0, MAKS_RADER);
    for (const b of vist) this.listeEl.append(this.rad(b));
    if (sortert.length > MAKS_RADER) {
      this.listeEl.append(varselSpan(
        `⚠ Viser de første ${MAKS_RADER} av ${sortert.length} — for mange til å vise på én gang.`));
    }
  }

  private rad(b: PersonBinding): HTMLElement {
    const row = div('targetlist-row');
    const dot = document.createElement('span');
    dot.className = 'checklist-dot';
    // Gull = «se hit» (samme aksent som tillitsmerkene i spor-panelet):
    // en uverifisert binding er en ventende handling for en overordnet.
    // Sage = bekreftet/i orden.
    dot.style.background = b.verifisert ? 'var(--sage)' : 'var(--gold)';

    const main = div('checklist-main');
    main.append(span('checklist-item-title', personEtikett(b, short(b.gnaMatrixId))));
    main.append(span('checklist-note',
      [short(b.gnaMatrixId), b.rolle, `node ${b.orbatNodeId}`].join(' · ')));
    main.append(span('checklist-note', statusTekst(b)));
    const feil = this.feilPerPerson.get(b.gnaMatrixId);
    if (feil) main.append(feilSpan(`⚠ ${feil}`));

    const actions = div('targetlist-actions');
    const knapp = document.createElement('button');
    knapp.type = 'button'; knapp.className = 'targetlist-btn';
    const sender = this.ventende.has(b.gnaMatrixId);
    knapp.disabled = sender;
    knapp.textContent = sender ? 'Sender …' : (b.verifisert ? 'Tilbakekall' : 'Verifiser');
    knapp.onclick = () => void this.handle(b);
    actions.append(knapp);

    row.append(main, actions);
    return row;
  }

  /**
   * Verifiser eller tilbakekall ÉN person. Suksess speiler bindingen HUBBEN
   * returnerte (aldri en gjettet lokal tilstand — myndighetssjekken skjedde
   * server-side, og hva som faktisk ble skrevet er hubbens sannhet).
   */
  private async handle(b: PersonBinding): Promise<void> {
    const sti = b.verifisert ? '/admin/medlemskap/tilbakekall' : '/admin/medlemskap/verifiser';
    this.ventende.add(b.gnaMatrixId);
    this.feilPerPerson.delete(b.gnaMatrixId);
    this.tegnListe();
    try {
      const svar = (await this.hooks.send(sti, { gnaMatrixId: b.gnaMatrixId })) as { binding?: PersonBinding };
      if (svar?.binding) {
        this.siste = this.siste.map((x) => (x.gnaMatrixId === b.gnaMatrixId ? svar.binding! : x));
        this.ventende.delete(b.gnaMatrixId);
        this.tegnListe();
      } else {
        // Hubben svarte OK uten bindingen — hent hele lista på nytt heller
        // enn å gjette oss til en ny tilstand.
        this.ventende.delete(b.gnaMatrixId);
        await this.last();
      }
    } catch (err) {
      // ⚠ DETTE ER SERVERENS EGEN AVSLAGSGRUNN (VERIFISERING_AVSLAG_TEKST),
      // ALLEREDE SKREVET FOR ET MENNESKE Å LESE — ikke en generisk «avvist».
      this.ventende.delete(b.gnaMatrixId);
      this.feilPerPerson.set(b.gnaMatrixId, feilTekst(err));
      this.tegnListe();
    }
  }
}

/** Statuslinja: verifisert-av + når, eller «Ikke verifisert». */
function statusTekst(b: PersonBinding): string {
  if (!b.verifisert) return 'Ikke verifisert';
  const av = b.verifisertAv ? short(b.verifisertAv) : 'ukjent overordnet';
  const naar = b.verifisertMs ? new Date(b.verifisertMs).toLocaleString('nb-NO') : '';
  return `Verifisert av ${av}${naar ? ' · ' + naar : ''}`;
}

// ── Nettverk (standardimplementasjonen main.ts sender inn) ───────────────────

/** Les feilen ut av hubbens JSON-svar når den er der; ellers statuskoden. */
async function feilAvSvar(r: Response): Promise<string> {
  try {
    const j = (await r.json()) as { error?: unknown };
    if (typeof j?.error === 'string') return `${r.status} ${j.error}`;
  } catch { /* ikke JSON — statuskoden er alt vi har */ }
  return `${r.status} ${r.statusText || 'feil'}`;
}

export async function personregisterHent(sti: string): Promise<unknown> {
  const r = await fetch(sti, { credentials: 'include' });
  if (!r.ok) throw new Error(await feilAvSvar(r));
  return r.json();
}

export async function personregisterSend(sti: string, kropp: unknown): Promise<unknown> {
  const r = await fetch(sti, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(kropp),
  });
  if (!r.ok) throw new Error(await feilAvSvar(r));
  return r.json();
}

function feilTekst(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

// ── Små DOM-hjelpere (samme sett som de andre panelene) ──────────────────────
function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
function span(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
/** Et forbehold (ufullstendig/avkuttet svar, men lista er fortsatt brukbar) —
 * gull-aksenten («se hit»), samme prinsipp som tillitsmerkene i spor-panelet
 * (UX_DESIGNSYSTEM §9b). Ingen ny hardkodet verdi: `--gold` er samme token
 * som resten av designsystemet. */
function varselSpan(text: string): HTMLElement {
  const e = span('checklist-note', text);
  e.style.color = 'var(--gold)';
  return e;
}
/** En FEIL — hentingen/handlingen slo faktisk feil (nettverk, 403 osv.), til
 * forskjell fra `varselSpan` sitt «ufullstendig, men brukbart». Bruker
 * `--danger`, samme alarm-token som resten av designsystemet — ALDRI en
 * hardkodet rød (jf. den gamle `.admin-err`-fargen §1). */
function feilSpan(text: string): HTMLElement {
  const e = span('checklist-note', text);
  e.style.color = 'var(--danger)';
  return e;
}
function short(id: string): string { return id.replace(/^@/, '').replace(/:.*$/, ''); }
